import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { LayoutDashboard, LogOut, CreditCard } from "lucide-react";
import clsx from "clsx";

const SuperAdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const isActive = (path: string) => location.pathname.includes(path);

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* Sidebar */}
      <aside className="w-72 bg-white text-white flex flex-col shadow-xl z-20">
        {/* Branding */}
        <div className="p-8 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center">
              <span className="font-bold text-white text-lg">D</span>
            </div>
            <h1 className="text-xl font-bold tracking-tight text-gray-100">DiagnomIQ</h1>
          </div>
          <p className="text-xs text-gray-400 mt-2 uppercase tracking-wider font-medium opacity-70">
            Super Admin
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <SidebarItem
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            active={isActive("/dashboard") || location.pathname === "/super-admin"}
            onClick={() => navigate("/dashboard")}
          />
          <SidebarItem
            icon={<CreditCard size={20} />}
            label="Subscription"
            active={isActive("/super-admin/subscription")}
            onClick={() => navigate("/super-admin/subscription")}
          />
        </nav>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-red-400 rounded-lg hover:bg-gray-800/50 transition-colors duration-200 group"
          >
            <LogOut size={20} className="group-hover:text-red-300 transition-colors" />
            <span className="group-hover:text-red-300 transition-colors">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8 lg:p-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

/* --- Sidebar Item Component --- */
const SidebarItem = ({
  icon,
  label,
  active,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={clsx(
      "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group",
      active
        ? "bg-primary-600 text-white shadow-lg shadow-primary-900/20"
        : "text-gray-400 hover:bg-gray-800 hover:text-white"
    )}
  >
    <span className={clsx("transition-colors", active ? "text-white" : "text-gray-400 group-hover:text-white")}>
      {icon}
    </span>
    {label}
  </button>
);

export default SuperAdminLayout;
